import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import ReminderPicker from "../../shared/tasks/components/ReminderPicker";
import RepeatAdvancedFields from "../../shared/tasks/components/RepeatAdvancedFields";
import TaskChecklist from "../../shared/tasks/components/TaskChecklist";
import TaskTemplatePicker from "../../shared/tasks/components/TaskTemplatePicker";
import { appendReminderFields } from "../../shared/tasks/utils/taskReminderSync";
import { templateToForm } from "../../shared/tasks/utils/taskTemplates";

const TaskModal = ({ isOpen, setIsOpen, leadOptions, assignOptions, fetchTask, onTaskCreated, applyTemplate, onApplied }) => {
    const today = new Date();
    const [dueDate, setDueDate] = useState(today);
    const [startTime, setStartTime] = useState();
    const [startDate, setStartDate] = useState(new Date());
    const [repetition, setRepetition] = useState(null);
    const [customDate, setCustomDate] = useState(null);
    const [title, setTitle] = useState("");
    const [priority, setPriority] = useState(null);
    const [list, setList] = useState(null);
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [checklist, setChecklist] = useState([]);
    const [reminderPreset, setReminderPreset] = useState("none");
    const [reminderCustomMinutes, setReminderCustomMinutes] = useState(15);
    const [repeatInterval, setRepeatInterval] = useState(1);
    const [repeatDays, setRepeatDays] = useState([]);
    const [repeatEndType, setRepeatEndType] = useState("never");
    const [repeatEndDate, setRepeatEndDate] = useState("");

    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";

    const repetitionOptions = [
        { value: "none", label: "Does not repeat" },
        { value: "daily", label: "Daily" },
        { value: "weekdays", label: "Weekdays" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "annually", label: "Yearly" },
        { value: "custom", label: "Custom" },
    ];

    const priorityOptions = [
        { value: "high", label: "High" },
        { value: "medium", label: "Medium" },
        { value: "low", label: "Low" },
    ];

    const getToken = () => decryptToken("accessToken") || "";

    const applyFields = (tpl) => {
        const fields = templateToForm(tpl);
        if (!fields) return;
        setTitle(fields.title);
        setNotes(fields.notes);
        setChecklist(fields.checklist);
        setPriority(priorityOptions.find((o) => o.value === fields.priority) || null);
        setRepetition(repetitionOptions.find((o) => o.value === fields.repetition) || null);
        setReminderPreset(fields.reminderPreset);
        setReminderCustomMinutes(fields.reminderCustomMinutes);
        if (fields.listId && Array.isArray(leadOptions)) {
            const match = leadOptions.find((o) => String(o.value) === String(fields.listId));
            if (match) setList(match);
        }
        toast.success(`Applied “${tpl.name}”`);
    };

    useEffect(() => {
        if (!isOpen || !applyTemplate) return;
        applyFields(applyTemplate);
        onApplied?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, applyTemplate]);

    const toIsoDate = (value) => {
        const date = value instanceof Date ? value : new Date(value || Date.now());
        return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim()) return toast.error("Title is required");

        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("due_date", toIsoDate(dueDate));
            formData.append("priority", priority?.value || "medium");
            formData.append("repetition", repetition?.value || "none");
            if (repetition?.value === "custom" && customDate) {
                formData.append("customDate", toIsoDate(customDate));
            }
            formData.append("list_id", list?.value || "");
            formData.append("notes", notes || "");
            formData.append("start_time", startTime || "09:00");
            formData.append("start_date", toIsoDate(startDate || dueDate));
            appendReminderFields(formData, reminderPreset, reminderCustomMinutes);
            if (repeatInterval > 1) formData.append("repeat_interval", String(repeatInterval));
            if (repeatDays.length) formData.append("repeat_days", JSON.stringify(repeatDays));
            if (repeatEndType) formData.append("repeat_end_type", repeatEndType);
            if (repeatEndDate) formData.append("repeat_end_date", repeatEndDate);
            if (file) {
                formData.append("attachment", file);
            }
            if (checklist.length > 0) {
                formData.append("checklist", JSON.stringify(checklist));
            }

            const response = await fetch(`${url}/task/create-task`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const res = await response.json();
            if (response.ok) {
                toast.success("Task created successfully!");
                setTitle("");
                setDueDate(today);
                setStartDate(today);
                setRepetition(null);
                setCustomDate(null);
                setPriority(null);
                setList(null);
                setNotes("");
                setFile(null);
                setChecklist([]);
                setStartTime("");
                fetchTask?.();
                onTaskCreated?.(res.task);
                setIsOpen(false);
            } else {
                toast.error(res.error || res.message || "Failed to create task");
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="task-modal-overlay" onClick={() => setIsOpen(false)}>
            <div className="task-modal-panel" onClick={(e) => e.stopPropagation()}>
                <h2 className="oms-modal-title">Create New Task</h2>

                <form className="oms-modal-form task-modal-form" onSubmit={handleSubmit}>
                    <div className="task-modal-form__body">
                    <TaskTemplatePicker
                        apiUrl={url}
                        getToken={getToken}
                        onApply={applyFields}
                    />
                    <div className="oms-form-group">
                        <label htmlFor="task-title" className="oms-form-label">
                            Title
                        </label>
                        <input
                            id="task-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter task title"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="oms-form-group">
                            <label className="oms-form-label">Start Date</label>
                            <Flatpickr
                                value={startDate}
                                options={{ minDate: "today" }}
                                onChange={(date) => setStartDate(date[0])}
                            />
                        </div>
                        <div className="oms-form-group">
                            <label className="oms-form-label">Due Date</label>
                            <Flatpickr
                                value={dueDate}
                                options={{ minDate: "today" }}
                                onChange={(date) => setDueDate(date[0])}
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Priority</label>
                            <Select
                                options={priorityOptions}
                                value={priority}
                                onChange={setPriority}
                                placeholder="Select priority"
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Due Time</label>
                            <Flatpickr
                                options={{
                                    enableTime: true,
                                    noCalendar: true,
                                    dateFormat: "H:i",
                                    time_24hr: true,
                                    defaultDate: "09:00",
                                }}
                                value={startTime}
                                onChange={(date, dateStr) => setStartTime(dateStr)}
                                placeholder="Select due time"
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Reminder</label>
                            <ReminderPicker
                                value={reminderPreset}
                                customMinutes={reminderCustomMinutes}
                                onChange={({ preset, customMinutes }) => {
                                    setReminderPreset(preset);
                                    if (customMinutes) setReminderCustomMinutes(customMinutes);
                                }}
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Repetition</label>
                            <Select
                                options={repetitionOptions}
                                value={repetition}
                                onChange={setRepetition}
                                placeholder="Select repetition"
                            />

                            {repetition?.value === "custom" && (
                                <Flatpickr
                                    value={customDate}
                                    options={{ minDate: "today" }}
                                    onChange={(date) => setCustomDate(date[0])}
                                    placeholder="Pick custom date"
                                />
                            )}

                            {repetition?.value && repetition.value !== "none" && (
                                <RepeatAdvancedFields
                                    repetition={repetition.value}
                                    repeatInterval={repeatInterval}
                                    repeatDays={repeatDays}
                                    repeatEndType={repeatEndType}
                                    repeatEndDate={repeatEndDate}
                                    onChange={(patch) => {
                                        if (patch.repeat_interval != null) setRepeatInterval(patch.repeat_interval);
                                        if (patch.repeat_days) setRepeatDays(patch.repeat_days);
                                        if (patch.repeat_end_type) setRepeatEndType(patch.repeat_end_type);
                                        if (patch.repeat_end_date != null) setRepeatEndDate(patch.repeat_end_date);
                                    }}
                                />
                            )}
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Add to List</label>
                            <Select
                                options={leadOptions}
                                value={list}
                                onChange={setList}
                                placeholder="Select list"
                                getOptionLabel={(option) => (
                                    <div className="flex items-center">
                                        <span
                                            className="inline-block w-3 h-3 rounded-full mr-2 shrink-0"
                                            style={{ backgroundColor: option.color }}
                                        />
                                        {option.label}
                                    </div>
                                )}
                                getOptionValue={(option) => option.value}
                            />
                        </div>
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Enter notes"
                            rows={3}
                        />
                    </div>

                    <div className="oms-form-group">
                        <TaskChecklist
                            items={checklist}
                            onChange={setChecklist}
                            canEdit
                            canToggle={false}
                        />
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Attachment</label>
                        <input type="file" onChange={(e) => setFile(e.target.files[0])} />
                    </div>
                    </div>

                    <div className="task-modal-actions">
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="task-modal-btn task-modal-btn--ghost"
                        >
                            Cancel
                        </button>
                        <button type="submit" className="task-modal-btn task-modal-btn--primary">
                            Create Task
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskModal;
